<template>
  <div class="staff-permission-management">
    <div class="page-header">
      <h2>店员权限管理</h2>
      <p>按门店管理员工账号，分配收银、会员和设备操作权限</p>
    </div>

    <div class="page-content">
      <div class="search-section">
        <div class="search-box">
          <select v-model="searchStoreId" class="store-select" @change="handleSearch">
            <option value="">全部门店</option>
            <option v-for="store in storeList" :key="store.id" :value="store.id">{{ store.storeName }}</option>
          </select>
          <input v-model="searchKeyword" class="search-input" placeholder="搜索用户名/姓名/手机号" @keyup.enter="handleSearch" />
          <button class="search-btn" @click="handleSearch">搜索</button>
          <button class="reset-btn" @click="handleReset">重置</button>
          <button class="add-btn" @click="handleAdd">新增店员</button>
        </div>
      </div>

      <div class="table-section">
        <div class="table-header">
          <h3>店员列表</h3>
        </div>
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>用户名</th>
                <th>姓名</th>
                <th>手机号</th>
                <th>所属门店</th>
                <th>身份</th>
                <th>权限</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="8" class="empty-cell">加载中...</td>
              </tr>
              <tr v-else-if="staffList.length === 0">
                <td colspan="8" class="empty-cell">暂无店员</td>
              </tr>
              <tr v-else v-for="staff in staffList" :key="staff.id">
                <td>{{ staff.username }}</td>
                <td>{{ staff.realName || staff.nickname || '-' }}</td>
                <td>{{ staff.phone || '-' }}</td>
                <td>{{ getStoreName(staff.storeId) }}</td>
                <td>
                  <span :class="['role-badge', isStoreAdminStaff(staff) ? 'admin' : 'employee']">
                    {{ isStoreAdminStaff(staff) ? '门店管理员' : '普通店员' }}
                  </span>
                </td>
                <td>
                  <span class="permission-summary">{{ getPermissionSummary(staff.employeePermissions) }}</span>
                </td>
                <td>
                  <span :class="['status-badge', isStaffEnabled(staff) ? 'active' : 'inactive']">
                    {{ isStaffEnabled(staff) ? '启用' : '禁用' }}
                  </span>
                </td>
                <td>
                  <button
                    v-if="!isStoreAdminStaff(staff)"
                    class="action-btn edit-btn"
                    @click="handleEdit(staff)"
                  >
                    编辑权限
                  </button>
                  <button class="action-btn admin-btn" @click="toggleStoreAdminRole(staff)">
                    {{ isStoreAdminStaff(staff) ? '取消管理员' : '设为管理员' }}
                  </button>
                  <button v-if="!isStoreAdminStaff(staff)" class="action-btn status-btn" @click="toggleStatus(staff)">
                    {{ isStaffEnabled(staff) ? '禁用' : '启用' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ editingStaff.id ? '编辑店员权限' : '新增店员' }}</h3>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <form class="staff-form" @submit.prevent="submitStaff">
            <div class="form-grid">
              <div class="form-group">
                <label class="required">用户名</label>
                <input v-model="editingStaff.username" class="form-input" :disabled="!!editingStaff.id" required />
              </div>
              <div class="form-group" v-if="!editingStaff.id">
                <label class="required">初始密码</label>
                <input v-model="editingStaff.password" type="password" class="form-input" required />
              </div>
              <div class="form-group">
                <label>姓名</label>
                <input v-model="editingStaff.realName" class="form-input" />
              </div>
              <div class="form-group">
                <label>手机号</label>
                <input v-model="editingStaff.phone" class="form-input" />
              </div>
              <div class="form-group">
                <label class="required">所属门店</label>
                <select v-model="editingStaff.storeId" class="form-select" required>
                  <option value="">请选择门店</option>
                  <option v-for="store in storeList" :key="store.id" :value="store.id">{{ store.storeName }}</option>
                </select>
              </div>
            </div>

            <div class="permission-section">
              <div v-for="group in permissionGroups" :key="group.title" class="permission-group">
                <h4>{{ group.title }}</h4>
                <div class="permission-grid">
                  <label v-for="permission in group.items" :key="permission.value" class="permission-item">
                    <input v-model="editingStaff.employeePermissions" type="checkbox" :value="permission.value" />
                    <span>{{ permission.label }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" class="cancel-btn" @click="closeModal">取消</button>
              <button type="submit" class="save-btn" :disabled="saving">{{ saving ? '保存中...' : '保存' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

const PERMISSION_GROUPS = [
  {
    title: '会员与收银',
    items: [
      { value: 'MEMBER_VIEW', label: '查看会员' },
      { value: 'MEMBER_CREATE', label: '微信开卡' },
      { value: 'MEMBER_EDIT', label: '编辑会员' },
      { value: 'MEMBER_RECHARGE', label: '会员充值' },
      { value: 'MEMBER_DEDUCT', label: '会员扣费' }
    ]
  },
  {
    title: '设备操作',
    items: [
      { value: 'DEVICE_START', label: '设备开机' },
      { value: 'DEVICE_EXTEND', label: '设备续时' },
      { value: 'DEVICE_END', label: '设备下机' },
      { value: 'DEVICE_SWITCH', label: '设备换机' },
      { value: 'DEVICE_STATUS_BOARD_EDIT', label: '编辑设备状态看板' }
    ]
  },
  {
    title: '订单与经营',
    items: [
      { value: 'ORDER_REFUND', label: '订单退款' },
      { value: 'ORDER_REVOKE', label: '订单撤销' },
      { value: 'STATISTICS_VIEW', label: '查看报表' },
      { value: 'STORE_SETTING', label: '门店设置' },
      { value: 'FREE_START', label: '免费上机' },
      { value: 'FREE_EXTEND', label: '免费延时' }
    ]
  }
]

export default {
  name: 'StaffPermissionManagement',
  data() {
    return {
      staffList: [],
      storeList: [],
      searchStoreId: '',
      searchKeyword: '',
      loading: false,
      saving: false,
      showModal: false,
      editingStaff: this.createEmptyStaff(),
      permissionGroups: PERMISSION_GROUPS
    }
  },
  mounted() {
    this.loadStoreList()
    this.loadStaffList()
  },
  methods: {
    createEmptyStaff() {
      return {
        id: null,
        username: '',
        password: '',
        realName: '',
        phone: '',
        storeId: '',
        employeePermissions: [],
        status: true
      }
    },
    normalizePermissions(value) {
      if (Array.isArray(value)) return value
      if (!value) return []
      return String(value).split(',').map(item => item.trim()).filter(Boolean)
    },
    normalizeStaff(raw) {
      const enabled = raw.enabled === true || raw.status === true || raw.status === 'NORMAL' || raw.status === 0
      return {
        ...raw,
        id: raw.id || raw.staffId || raw.userId,
        realName: raw.realName || raw.nickName || raw.nickname || '',
        nickname: raw.nickname || raw.nickName || raw.username || '',
        status: enabled,
        employeePermissions: this.normalizePermissions(raw.employeePermissions || raw.permissions)
      }
    },
    isStoreAdminStaff(staff) {
      return !!(staff && (staff.storeAdmin === true || staff.role === 'STORE_ADMIN'))
    },
    isStaffEnabled(staff) {
      return staff && (staff.status === true || staff.status === 'NORMAL' || staff.status === 0 || staff.enabled === true)
    },
    async loadStoreList() {
      const response = await request.get('/api/stores/getStores', { params: { page: 0, size: 1000 } })
      if (response.data.success && response.data.code === 200) {
        this.storeList = response.data.data.content || []
      }
    },
    async loadStaffList() {
      this.loading = true
      try {
        const response = await request.get('/api/miniapp/admin/staff', {
          params: {
            storeId: this.searchStoreId || undefined
          }
        })
        if (response.data.success && response.data.code === 200) {
          const rawList = Array.isArray(response.data.data) ? response.data.data : (response.data.data?.content || [])
          const keyword = String(this.searchKeyword || '').trim().toLowerCase()
          this.staffList = rawList
            .map(this.normalizeStaff)
            .filter(staff => {
              if (!keyword) return true
              return [staff.username, staff.realName, staff.nickname, staff.phone]
                .some(value => String(value || '').toLowerCase().includes(keyword))
            })
        }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.loadStaffList()
    },
    handleReset() {
      this.searchStoreId = ''
      this.searchKeyword = ''
      this.loadStaffList()
    },
    handleAdd() {
      this.editingStaff = this.createEmptyStaff()
      this.showModal = true
    },
    handleEdit(staff) {
      if (this.isStoreAdminStaff(staff)) {
        alert('门店管理员拥有门店管理权限，如需按普通店员分配权限，请先取消管理员身份')
        return
      }
      this.editingStaff = {
        ...staff,
        password: '',
        employeePermissions: this.normalizePermissions(staff.employeePermissions)
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editingStaff = this.createEmptyStaff()
    },
    getStoreName(storeId) {
      const store = this.storeList.find(item => String(item.id) === String(storeId))
      return store ? store.storeName : '-'
    },
    getPermissionSummary(permissions) {
      const list = this.normalizePermissions(permissions)
      return list.length ? `${list.length} 项权限` : '未授权'
    },
    async submitStaff() {
      this.saving = true
      try {
        const payload = this.editingStaff.id ? {
          storeId: this.editingStaff.storeId,
          permissions: this.editingStaff.employeePermissions
        } : {
          username: this.editingStaff.username,
          password: this.editingStaff.password,
          realName: this.editingStaff.realName,
          phone: this.editingStaff.phone,
          role: 'EMPLOYEE',
          storeId: this.editingStaff.storeId,
          status: this.editingStaff.status !== false,
          employeePermissions: this.editingStaff.employeePermissions,
          managedStoreIds: []
        }
        const response = this.editingStaff.id
          ? await request.put(`/api/miniapp/admin/staff/${this.editingStaff.id}/permissions`, payload)
          : await request.post('/api/users/createUser', payload)
        if (response.data.success && response.data.code === 200) {
          this.closeModal()
          this.loadStaffList()
        } else {
          throw new Error(response.data.message || '保存失败')
        }
      } catch (error) {
        alert(error.message || '保存失败')
      } finally {
        this.saving = false
      }
    },
    async toggleStatus(staff) {
      const nextStatus = !this.isStaffEnabled(staff)
      const response = await request.put(`/api/miniapp/admin/staff/${staff.id}/status`, {
        storeId: staff.storeId,
        enabled: nextStatus
      })
      if (response.data.success && response.data.code === 200) {
        staff.status = nextStatus
        staff.enabled = nextStatus
      }
    },
    async toggleStoreAdminRole(staff) {
      const enabled = !this.isStoreAdminStaff(staff)
      const confirmMessage = enabled
        ? `确认将“${staff.realName || staff.nickname || staff.username}”设为门店管理员？`
        : `确认取消“${staff.realName || staff.nickname || staff.username}”的门店管理员身份？`
      if (!window.confirm(confirmMessage)) {
        return
      }
      try {
        const response = await request.put(`/api/miniapp/admin/staff/${staff.id}/store-admin`, {
          storeId: staff.storeId,
          enabled
        })
        if (response.data.success && response.data.code === 200) {
          const updated = this.normalizeStaff(response.data.data)
          const index = this.staffList.findIndex(item => String(item.id) === String(staff.id))
          if (index >= 0) {
            this.staffList.splice(index, 1, updated)
          }
        } else {
          throw new Error(response.data.message || '身份修改失败')
        }
      } catch (error) {
        alert(error.response?.data?.message || error.message || '身份修改失败')
        this.loadStaffList()
      }
    }
  }
}
</script>

<style scoped>
.page-header { margin-bottom: 24px; }
.page-header h2 { margin: 0 0 8px; color: #333; }
.page-header p { margin: 0; color: #666; }
.page-content, .table-section { background: #fff; border-radius: 8px; padding: 20px; }
.search-section { margin-bottom: 20px; }
.search-box { display: flex; gap: 12px; flex-wrap: wrap; }
.search-input, .store-select, .form-input, .form-select { height: 36px; padding: 0 12px; border: 1px solid #d9d9d9; border-radius: 6px; }
.search-input { width: 220px; }
button { border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; }
.search-btn, .add-btn, .save-btn { background: #1890ff; color: #fff; }
.reset-btn, .cancel-btn { background: #f5f5f5; color: #333; }
.table-container { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th, .data-table td { padding: 12px; border-bottom: 1px solid #f0f0f0; text-align: left; }
.empty-cell { text-align: center; color: #999; padding: 40px; }
.status-badge { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
.status-badge.active { background: #f6ffed; color: #52c41a; }
.status-badge.inactive { background: #fff7e6; color: #fa8c16; }
.role-badge { padding: 4px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.role-badge.admin { background: #f0f5ff; color: #2f54eb; }
.role-badge.employee { background: #f6ffed; color: #389e0d; }
.action-btn { margin-right: 8px; padding: 4px 8px; font-size: 12px; }
.edit-btn { background: #e6f7ff; color: #1890ff; }
.admin-btn { background: #f0f5ff; color: #2f54eb; }
.status-btn { background: #fff7e6; color: #fa8c16; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-content { width: 760px; max-height: 88vh; overflow: auto; background: #fff; border-radius: 8px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #f0f0f0; }
.close-btn { background: transparent; font-size: 22px; padding: 0; }
.modal-body { padding: 20px; }
.form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.form-group label { display: block; margin-bottom: 8px; color: #333; }
.required::after { content: ' *'; color: #ff4d4f; }
.permission-section { margin-top: 20px; }
.permission-group { margin-bottom: 16px; }
.permission-group h4 { margin: 0 0 10px; }
.permission-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.permission-item { padding: 8px; background: #f7f8fa; border-radius: 4px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 20px; }
</style>
